import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isChildLogin, setIsChildLogin] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [childLoginData, setChildLoginData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      
      // Role-based redirect
      const role = response.data.user.role;
      if (role === 'admin') {
        navigate('/admin');
      } else if (role === 'test_admin') {
        navigate('/test-admin');
      } else {
        navigate('/parent/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChildLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/child-login`, null, {
        params: {
          username: childLoginData.username,
          password: childLoginData.password
        }
      });
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(`Welcome back, ${response.data.user.name}!`);
      navigate(`/child/dashboard/${response.data.user.child_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-background to-muted">
      <div className="w-full max-w-md">
        <div className="card-base p-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
              {isChildLogin ? 'Child Login' : (isLogin ? 'Welcome Back' : 'Get Started')}
            </h1>
            <p className="text-muted-foreground">
              {isChildLogin ? 'Enter your username and password' : (isLogin ? 'Sign in to your account' : 'Create your parent account')}
            </p>
          </div>

          {isChildLogin ? (
            <form onSubmit={handleChildLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  placeholder="Your first name"
                  value={childLoginData.username}
                  onChange={(e) => setChildLoginData({ ...childLoginData, username: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="childPassword">Password</Label>
                <Input
                  id="childPassword"
                  data-testid="child-password-input"
                  type="password"
                  value={childLoginData.password}
                  onChange={(e) => setChildLoginData({ ...childLoginData, password: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <Button
                data-testid="child-login-btn"
                type="submit"
                className="w-full rounded-full py-6"
                disabled={loading}
              >
                {loading ? 'Please wait...' : 'Login'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsChildLogin(false)}
                  className="text-primary hover:underline font-semibold"
                >
                  Parent/Admin Login
                </button>
              </div>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              className="w-full rounded-full py-6"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary hover:underline font-semibold block w-full"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
            <button
              onClick={() => setIsChildLogin(true)}
              className="text-accent hover:underline font-semibold"
            >
              Child Login →
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;